import type { Plan, PlanLimit, Product, Subscription, UsageFee } from '../api/types'
import { formatNumberN } from './format'

/** Plan `limits` as returned by the backend may be `PlanLimit[]`, a bare
 * `number[]` (admin-by-email view, b3.admin.plans.by-email) or contain junk
 * elements (b3b.plans.create.json: `-1`; b3b.plans.modify: `"abc"`) — the
 * legacy exporter's own quirks. Only well-formed `{key, value}` entries are
 * shown. */
export function planLimits(plan: Pick<Plan, 'limits'>): PlanLimit[] {
  const raw = Array.isArray(plan.limits) ? (plan.limits as unknown[]) : []
  return raw.filter(
    (l): l is PlanLimit => !!l && typeof l === 'object' && typeof (l as PlanLimit).key === 'string' && (l as PlanLimit).key !== '',
  )
}

export const SUBSCRIPTION_STATUS = {
  NOT_STARTED: 0,
  ACTIVE: 1,
  UNPAID: 3,
  CANCELED: 6,
  INACTIVE: 9,
} as const

/** Legacy subscription status label (admin-plan-list "Subscribers" table,
 * admin-user-subscription-list): 1 Active, 3 Overdue, 6 Canceled, expired
 * flag wins as "Expired"; 0 and 9 render blank. */
export function subscriptionStatusLabel(sub: Pick<Subscription, 'status' | 'expired'> | null | undefined): string {
  if (!sub) return ''
  if (sub.expired && Number(sub.status) !== 0 && Number(sub.status) !== 9) return 'Expired'
  switch (Number(sub.status)) {
    case 1:
      return 'Active'
    case 3:
      return 'Overdue'
    case 6:
      return 'Canceled'
    default:
      return ''
  }
}

export function subscriptionRowClass(sub: Pick<Subscription, 'status' | 'expired'> | null | undefined): string | undefined {
  if (!sub) return undefined
  const s = Number(sub.status)
  if (s === 1 && !sub.expired) return 'fs-row--success'
  if (s === 3 && !sub.expired) return 'fs-row--danger'
  if (s === 6 || (sub.expired && s !== 0 && s !== 9)) return 'fs-row--warning'
  return undefined
}

export const CURRENCIES = ['usd', 'eur', 'gbp', 'huf'] as const

/** Legacy transaction labels (payment-transaction-list /
 * admin-user-transaction-list). */
export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  monthlyfee: 'Monthly fee',
  weeklyfee: 'Weekly commission fee',
  adhoc: 'Ad-hoc',
  onetime: 'Onetime payment',
  plan: 'Subscription',
  product: 'Product payment',
  credit: 'Credit buy',
  custom: 'Custom payment',
}

export const TRANSACTION_STATUS_LABELS: Record<number, string> = {
  0: 'Pending',
  1: 'Finished',
  6: 'Canceled',
  9: 'Failed',
}

export const TRANSACTION_CAUSE_LABELS: Record<string, string> = {
  setup: 'Setup',
  pmchange: 'Payment method change',
  manual: 'Manual',
  renew: 'Renew',
  startup: 'Startup',
  serverinstall: 'Server install',
}

export const TRANSACTION_RESULT_LABELS: Record<number, string> = {
  0: 'Unknown',
  1: 'Success',
  2: 'Card expired',
  3: 'Transaction declined (authentication needed)',
  6: 'Canceled',
  9: 'Transaction declined',
}

/** Legacy `openPlanModal` rule: the PAY modal for a never-started plan with
 * a price, otherwise the VIEW modal. */
export function isPlanPayable(plan: Plan): boolean {
  const sub = plan.subscription
  return (Number(plan.price) > 0 && !sub) || (!!sub && Number(sub.status) === 0)
}


/** Legacy product-list "Pay" rule: a price, and either payable anytime
 * (`limit` 0) or payable once and not paid yet. */
export function isProductPayable(product: Product): boolean {
  return (
    Number(product.price) > 0 &&
    (Number(product.limit) === 0 || (!!product.onetimeproduct && !Number(product.onetimeproduct.status)))
  )
}

/** `USD 0.01 / 1000 calls - paid after every started block` */
export function usageFeePriceText(fee: UsageFee): string {
  const unit = Number(fee.unit)
  return `${fee.currency.toUpperCase()} ${formatNumberN(fee.price, 8)} / ${formatNumberN(unit, 8)} ${fee.unitName}${
    unit > 1 ? 's' : ''
  }${fee.fixUnits ? ' - paid after every started block' : ''}`
}


/** Credit-ledger vocabulary (payment-credit-view) — a DIFFERENT enum from
 * the payment transactions': result 0 = SUCCESS here. */
export const CREDIT_CAUSE_LABELS: Record<string, string> = {
  manual_inpayment: 'Credit buy',
  manual_payment: 'Pay with credit',
  admin_credit: 'Credit by admin',
  admin_correction: 'Correction by admin',
}

export const CREDIT_REFERENCE_LABELS: Record<string, string> = {
  setup: 'Payment setup',
  pmchange: 'Payment method change',
  manual: 'Manual payment',
}
